import React, { useEffect, useState } from 'react';
import { useBackHandler } from '../hooks/useBackHandler';

/**
 * **운영진이 고치는 안내 문구** — 화면 머리의 한 줄(예: 공략 탭 "길드 점령전 공략 게시글입니다").
 *
 * 서버 `GET/PUT /api/page-text/<키>` 에 한 줄로 저장한다. 저장된 게 없으면 `defaultText`.
 * 운영진(`canEdit`)에게만 문구 옆 연필이 보이고, 누르면 그 자리에서 고친다. **여러 줄**로 쓸 수 있다(사용자 지시) —
 * Enter 는 줄바꿈, Ctrl+Enter 저장, Esc·뒤로 가기 취소.
 * 실제 차단은 서버가 한다(운영진이 아니면 403).
 */
export default function EditablePageText({ pageKey, defaultText, canEdit, style }: {
    pageKey: string; defaultText: string; canEdit: boolean; style?: React.CSSProperties;
}) {
    const [text, setText] = useState(defaultText);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        let gone = false;
        fetch(`/api/page-text/${pageKey}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => { if (!gone && j?.success && j.text) setText(j.text); })
            .catch(() => {});
        return () => { gone = true; };
    }, [pageKey]);

    const cancel = () => { setEditing(false); setErr(''); };
    useBackHandler(editing, cancel);

    const save = async () => {
        const v = draft.trim();
        if (!v) { setErr('문구가 비었습니다'); return; }
        setSaving(true); setErr('');
        try {
            const r = await fetch(`/api/page-text/${pageKey}`, {
                method: 'PUT', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: v })
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok || !j.success) throw new Error(j.error || '저장하지 못했습니다');
            setText(j.text);
            setEditing(false);
        } catch (e: any) {
            setErr(e.message || '저장하지 못했습니다');
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <textarea
                    autoFocus
                    value={draft}
                    maxLength={1000}
                    rows={Math.min(10, Math.max(3, draft.split('\n').length + 1))}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); save(); }
                        if (e.key === 'Escape') cancel();
                    }}
                    style={{
                        width: '100%', maxWidth: 520, boxSizing: 'border-box', padding: '8px 12px', borderRadius: 10, fontSize: 14,
                        lineHeight: 1.6, border: '1.5px solid #667eea', outline: 'none', color: '#333', background: '#fff',
                        resize: 'vertical', fontFamily: 'inherit', textAlign: 'center'
                    }}
                />
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center' }}>
                    <button onClick={save} disabled={saving} style={{
                        padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', opacity: saving ? 0.6 : 1, whiteSpace: 'nowrap'
                    }}>{saving ? '저장 중' : '저장'}</button>
                    <button onClick={cancel} disabled={saving} style={{
                        padding: '8px 12px', borderRadius: 10, border: '1px solid #ddd', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                        background: '#fff', color: '#666', whiteSpace: 'nowrap'
                    }}>취소</button>
                </div>
                {err && <span style={{ color: '#e11d48', fontSize: 12, fontWeight: 600 }}>{err}</span>}
            </div>
        );
    }

    return (
        <p style={{ ...style, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <span style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{text}</span>
            {canEdit && (
                <button
                    onClick={() => { setDraft(text); setEditing(true); }}
                    aria-label="문구 고치기"
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 2, lineHeight: 0, color: '#8a8fb5', display: 'inline-flex' }}
                >
                    {/* 지우개 달린 연필 — 이모지 대신 직접 그림(사용자 2026-10-30 초안 4번). 글자색을 따른다 */}
                    <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" aria-hidden="true">
                        <path d="M4 20l1.2-4.6L15 5.6l3.4 3.4-9.8 9.8z" />
                        <path d="M15 5.6l1.6-1.6a1.6 1.6 0 0 1 2.3 0l1.1 1.1a1.6 1.6 0 0 1 0 2.3L18.4 9" fill="currentColor" />
                    </svg>
                </button>
            )}
        </p>
    );
}
